/*
 * @description:
 * @author: Jason Wong
 * @since: 2021-06-29 14:54:22
 */
import { makeQuery } from "@/api/base";
import axios from "axios";
import TokenManager from "@/utils/token/tokenManager";
import {
  ASSET_STATE,
  ASSET_PERMISSION,
  SYSTEM_RESPONSE_CODE,
  ASSET_FILE_STATE,
} from "@/views/common/enums";
import { isBlank } from "../../utils/String";
import CATUtils from "../../utils/catUtils";
import { uniq } from "lodash";

class DragObjectIdentity {
  constructor(identityObject) {
    this._object = identityObject || {};
    this._value = JSON.stringify(this._object);
  }

  get json() {
    return this._value;
  }

  get object() {
    return this._object;
  }

  set(keyValueMap) {
    if (!keyValueMap) return;
    for (const key in keyValueMap) {
      if (key in this._value._object) {
        this._object[key] = keyValueMap[key];
      }
    }
    this._value = JSON.stringify(this._object);
  }
}

/**
 * store student information who is draging
 */
class StudentDragIdentity extends DragObjectIdentity {
  constructor(ownerId, studentId) {
    super({ ownerId, studentId });
  }
}

const DRAG_CONTEXT_PREFIX = "$$drag_context_";
let DRAG_CONTEXT_GLOBAL_ID = 1;
/**
 * a key-objct map to resore object used in a draging session
 */
class DragContext {
  constructor() {
    this._idPrefix = `${DRAG_CONTEXT_PREFIX}${DRAG_CONTEXT_GLOBAL_ID++}_`;
    this._currentSerialNumber = 1;
    this._objectPool = {};
  }

  register(obj) {
    if (!obj) throw new Error("obj can not be null");
    const newId = `${this._idPrefix}_${this._currentSerialNumber++}`;
    this._objectPool[newId] = obj;
    return newId;
  }

  /**
   *
   * @param {} param0
   */
  transferDataOwnership({ from, to, dataCredentials }) {
    const owner = this.get(from);
    const recipient = this.get(to);
    if (!owner) {
      throw new Error("Owner does not exists.");
    }
    if (!recipient) {
      throw new Error("Object who want to recive can not be null");
    }

    const instance = owner.giveUpObject(dataCredentials);
    instance && recipient.reciveObject(instance);
  }

  unregister(key) {
    if (!key) return;
    if (key in this._objectPool) {
      delete this._objectPool[key];
    }
  }

  get(key) {
    if (!key) return null;
    return this._objectPool[key];
  }

  clear() {
    this._objectPool = {};
  }
}

const DRAG_CONTAINER = {
  data() {
    return {
      dragSessionId: null,
    };
  },
  mounted() {
    if (this.dragSession) {
      this.dragSessionId = this.dragSession.register(this);
    }
  },
  destroyed() {
    if (this.dragSession) {
      this.dragSession.unregister(this.dragSessionId);
    }
  },
};

const ASSET_TYPE = {
  TM: 0,
  GLOSSARY: 1,
};

async function refreshRows({ rows, api, param }) {
  if(!rows || rows.length == 0){
    return;
  }
  const ids = rows.map((row) => row.id);
  if(!ids || ids.length == 0){
    return;
  }
  const form = { [param]: ids };
  const result = await api.getCurrentState(form);
  if (Array.isArray(result) && result && result.length != 0) {
    let updateRowList = await updateRows(result, rows);
      return [
        updateRowList,
        result
      ];
  } else {
      console.error('api.getCurrentState 未返回数组:', result);
  }
}

function updateRows(newAssets, oldAssets) {
  newAssets.forEach((newAsset) => {
    const matched = oldAssets.find((oldAsset) => newAsset.id === oldAsset.id);
    const updatingFields = ["state", "stateMessage", "rowCount", "ateId"];
    updatingFields.forEach((field) => {
      matched[field] = newAsset[field];
    });
  });
  return newAssets;
  
}

const TM_EXCEL_TEMPLATE_URL = "/initDir/tm/tm_import_template.xlsx";
const TB_EXCEL_TEMPLATE_URL = "/saipan/export/template/tb_import_template.xlsx";

const TM_EXPORT_OPTIONS = {
  TMX: 0,
  XLSX: 1,
};

const TB_EXPORT_OPTIONS = {
  TBX: 0,
  XLSX: 1,
};

/**
 * 构建生成动态术语库模板的url
 * @param {*} langs 逗号分隔的语言
 * @returns
 */
function buildDynamicTemplate(langs) {
  const url = makeQuery(axios.defaults.baseURL + `/glossary/downloadTemplate`, {
    langs,
  });
  return url;
}

/**
 * 构建生成导出记忆库/术语库的url
 */
function buildExportUrl(type, id, format) {
  const typeRepr = Object.keys(ASSET_TYPE)[type];
  const url = makeQuery(
    axios.defaults.baseURL + `/${typeRepr.toLowerCase()}/export`,
    { id, format }
  );
  return url;
}

/**
 * 检查是否可以导入或导出记忆库/术语库
 * @param {*} state
 * @param {*} title
 * @param {*} action
 */
function validateAssetState(state, title, action) {
  if (state === ASSET_STATE.error) {
    throw new Error(`${title}预处理错误，无有效数据，无法${action}`);
  }
  if (state === ASSET_STATE.combiningError) {
    throw new Error(`${title}合并出错，无有效数据，无法${action}`);
  }
  if ([ASSET_STATE.processed, ASSET_STATE.combined].includes(state)) return;
  if (state < ASSET_STATE.processed) {
    throw new Error(`${title}已创建但还在预处理中，无法再${action}文件`);
  }
  if (state < ASSET_STATE.combined) {
    throw new ExportValidationError(
      `${title}已创建但还在合并中，无法再${action}文件`
    );
  }
}

/**
 * 记忆库/术语库共用的权限选项
 */
const permissionOptions = {
  private: {
    color: "#67C23A",
    label: "仅自己使用",
    value: ASSET_PERMISSION.private,
  },
  readonly: {
    color: "#409EFF",
    label: "共享但只读",
    value: ASSET_PERMISSION.readOnly,
  },
  public: {
    color: "#E6A23C",
    label: "共享且可改",
    value: ASSET_PERMISSION.public,
  },
};

/**
 * 记忆库或术语库导入通用验证类
 */
class AssetImportValidator {
  constructor(title) {
    this.title = title;
  }

  validate({ state }) {
    if (CATUtils.isTransferable({ state })) return;
    if (state === ASSET_STATE.error) {
      throw new ImportValidationError(
        `${this.title}预处理错误，无有效数据，无法导入`
      );
    }
    throw new ImportValidationError(
      `${this.title}已创建但还在预处理中，无法再导入文件`
    );
  }
}

class ImportValidationError extends Error {}

class ExportValidator {
  validate(row) {
    this.checkRowCount(row.rowCount);
  }

  checkRowCount(count) {
    if (!count) {
      throw new ExportValidationError("条目数为0，无有效数据，无法导出。");
    }
  }
}

/**
 * 记忆库或术语库导出通用验证类
 */
class AssetExportValidator extends ExportValidator {
  constructor(title) {
    super();
    this.title = title;
  }

  validate(row) {
    super.validate(row);
    this.checkState(row);
  }

  /**
   * 主库的状态
   * @param {*} row
   */
  checkState({ state }) {
    if (CATUtils.isTransferable({ state })) return;
    if ([ASSET_STATE.error].includes(state)) {
      throw new ExportValidationError(
        `${this.title}预处理错误，无有效数据，无法导出`
      );
    }
    throw new ExportValidationError(
      `${this.title}已创建但还在预处理中，此时无法再导出文件`
    );
  }
}

class ExportValidationError extends Error {}

// ↓ 单个文件导入详情统计弹出框的文案
const TM_IGNORE_CASES = [
  "原文或译文为空。",
  "源语言和目标语言与目标记忆库不一致。",
  "没有指定语言。",
  "原文和译文存在重复。",
];
const TB_IGNORE_CASES = [
  "术语全为空。",
  "语言与目标术语库不一致。",
  "没有指定语言。",
  "条目存在重复。",
];
const ERROR_CASES = [
  "文本长度超出限制。",
  "格式不符合要求。",
  "文件内容无法识别。",
];

class FileDownloadWarningError extends Error {}

/**
 * 导出记忆库/术语库相关文件
 * @param {*} request
 */
async function downloadFile(request) {
  const response = await request();
  if (
    response.status === 294 &&
    response.data.type.startsWith("application/json")
  ) {
    const responseStr = await new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.onerror = () => {
        fileReader.abort();
        reject(new Error("下载文件出错"));
      };
      fileReader.onload = () => {
        resolve(fileReader.result);
      };
      fileReader.readAsText(response.data);
    });
    throw new FileDownloadWarningError(JSON.parse(responseStr).error);
  }
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute(
    "download",
    decodeURIComponent(
      response.headers["content-disposition"].match(/(?<=filename=").*(?=")/)[0]
    )
  );
  link.click();
  window.URL.revokeObjectURL(url);
}

// 该方法可能会替代导出记忆库/术语库，待讨论
function exportAsset(type, id, format) {
  const typeRepr = Object.keys(ASSET_TYPE)[type];
  const path = `/${typeRepr.toLowerCase()}/export`;
  const accessToken = TokenManager.getAccessToken();
  axios
    .get(path, {
      headers: {
        TokenAuthorization: accessToken,
      },
      params: { id, format },
    })
    .then((res) => {
      if (res) {
        window.open(res.request.responseURL, "_self");
      }
    });
}

/**
 * 用于验证两个语言是否相同：如果一方语言已有不能相同的错误提示，则忽略验证另一语言
 * @param {Object} form el-form 组件实例
 * @param {Object} ob
 * @param {String} ob.src 源语言字段
 * @param {String} ob.tgt 源语言字段
 */
function getIgnoreLangField(form, { src, tgt }) {
  const srcLangField = form.fields.find((field) => field.prop === src);
  const destLangField = form.fields.find((field) => field.prop === tgt);
  const errorField = [srcLangField, destLangField].find(
    (field) => field.validateState === "error"
  );
  if (errorField !== undefined) {
    const toIgnoreField =
      errorField === srcLangField ? destLangField : srcLangField;
    return toIgnoreField;
  }
  return null;
}

const QA_LEVEL = {
  slight: 0,
  normal: 1,
  serious: 2,
};
function getQALevelText(level) {
  return ["轻微错误", "一般错误", "严重错误"][level];
}

/**
 * 用户操作（目前只有删除）
 */
const ASSET_ACTION = {
  delete: 0,
};

class AssetActionHandler {
  constructor(api) {
    this.api = api;
  }

  async validateAction(id, action) {
    let response;
    response = await this.api.validateAction({ id, action });
    if (response.success)
      return {
        success: true,
        type: "success",
        message: null,
      };
    if (response.code === SYSTEM_RESPONSE_CODE.warning) {
      return {
        success: false,
        type: "warning",
        message: response.message,
      };
    }
    return {
      success: false,
      type: "error",
      message: response.message,
    };
  }
}

const COMBINABLE_STATE = {
  ready: { code: 0, msg: "合并中" },
  combining: { code: 1, msg: "合并中" },
  combined: { code: 2, msg: "已合并" },
  error: { code: 3, msg: "合并出错" },
  canUse: { code: 4, msg: "可用" },
};

const TRANSFER_ITEM_TYPE = {
  file: { code: 0, key: "imported" },
  mergeSource: { code: 1, key: "merged" },
};

/**
 * 记忆库合并/导入的明细相关选项
 */
const transferOptions = {
  [TRANSFER_ITEM_TYPE.mergeSource.key]: {
    title: "合并",
    nameHeader: "合并源库",
    handlers: {
      getStatus(state,typeCode) {
          for (const stateOption of Object.values(COMBINABLE_STATE)) {
            if (stateOption.code === state) return stateOption.msg;
          }
          return "-";
      },
      getExtraDetails(data) {
        return this.extraDetails.map((item) => {
          return {
            field: item.field,
            value: data[item.key],
          };
        });
      },
      attachSpanOption(rows) {
        const grouped = rows.reduce((acc, cur) => {
          if (!acc[cur.mergeId]) acc[cur.mergeId] = [];
          acc[cur.mergeId].push(cur);
          return acc;
        }, {});
        const rowGroups = Object.values(grouped);
        rowGroups.forEach((group, i) => {
          const groupOrdinal = i + 1;
          group.forEach((row, i) => {
            if (i !== 0) {
              row.spanOption = {
                span: false,
                range: null,
                ordinal: null,
              };
              return;
            }
            row.spanOption = {
              span: true,
              range: group.length,
              ordinal: groupOrdinal,
            };
          });
        });
      },
    },
    extraDetails: [{ field: "合并源库", key: "name" }],
  },
  [[TRANSFER_ITEM_TYPE.file.key]]: {
    title: "导入",
    nameHeader: "文件名称",
    handlers: {
      getStatus(state,typeCode) {
        if (typeCode === ASSET_TYPE.TM) {
          switch (state) {
            case ASSET_FILE_STATE.uploading:
              return "预处理中";
            case ASSET_FILE_STATE.processing:
              return "预处理中";
            case ASSET_FILE_STATE.processed:
              return "已导入";
            case ASSET_FILE_STATE.error: 
              return "导入出错";
            case ASSET_FILE_STATE.canUse: 
              return "可用";  
          }
        }else{
          switch (state) {
            case ASSET_FILE_STATE.uploading:
              return "预处理中";
            case ASSET_FILE_STATE.processing:
              return "预处理中";
            case ASSET_FILE_STATE.error: 
              return "导入出错";
            case ASSET_FILE_STATE.canUse: 
              return "可用";  
          }
        }
      },
    },
  },
};

class TbMetadataValidator {
  static rules = {
    source: {
      label: "来源",
      limit: 1024,
      handler(text) {
        return text.trim().length > this.limit;
      },
      get message() {
        return `${this.label}长度不能超过${this.limit}字符`;
      },
    },
    description: {
      label: "描述",
      limit: 1024,
      handler(text) {
        return text.trim().length > this.limit;
      },
      get message() {
        return `${this.label}长度不能超过${this.limit}字符`;
      },
    },
  };

  static validate(text, key) {
    if (!text) return null;
    const rule = TbMetadataValidator.rules[key];
    const invalid = rule.handler(text);
    if (!invalid) return null;
    return rule.message;
  }
}

/**
 * 记忆库/术语库条目的单个单元格的验证
 */
class TbRowValidator {
  static GENERIC_MESSAGE =
    "术语无法保存，部分数据不符合规范，详见条目内错误提示";
  static MAX_CHARACTER_LENGTH = 250;
  static MAX_TERM_NUM = 20;
  static rules = {
    length: {
      label: "长度",
      limit: TbRowValidator.MAX_CHARACTER_LENGTH,
      handler(text) {
        return text.trim().length > this.limit;
      },
      get message() {
        return `单个术语长度不能超过${this.limit}字符`;
      },
    },
    number: {
      label: "个数",
      limit: TbRowValidator.MAX_TERM_NUM,
      handler(text) {
        return text.split(",").filter(item => !isBlank(item)).length > this.limit;
      },
      get message() {
        return `单条术语条目个数不能超过${this.limit}`;
      },
    },
  };

  static VALIDATION_OPTIONS = {
    1: { validationKey: "term1Validation" },
    2: { validationKey: "term2Validation" },
  };

  attachValidationOption(row) {
    Object.values(TbRowValidator.VALIDATION_OPTIONS).forEach((option) => {
      row[option.validationKey] = {
        valid: true,
        message: null,
      };
    });
  }

  validate(row) {
    const cells = Object.keys(TbRowValidator.VALIDATION_OPTIONS).map((key) => {
      return {
        ...TbRowValidator.VALIDATION_OPTIONS[key],
        value: row[`term${key}`] || "",
      };
    });
    for (const cell of cells) {
      const { value, validationKey } = cell;
      const validationOption = row[validationKey];
      validationOption.valid = true;
      validationOption.message = null;

      for (const rule of Object.values(TbRowValidator.rules)) {
        const invalid = rule.handler(value);
        if (!invalid) continue;
        validationOption.valid = false;
        validationOption.message = rule.message;
      }
    }
    if (cells.some(({ validationKey }) => !row[validationKey].valid)) return;
    // consider comma-sperated cases
    const isAllEmpty = cells.every(({ value }) =>
      isBlank(value.split(",").join("").trim())
    );
    if (isAllEmpty) {
      cells.forEach(({ validationKey }) => {
        row[validationKey].valid = false;
        row[validationKey].message = "至少保证一个语言术语不为空";
      });
      return;
    }
    cells.forEach(({ validationKey }) => {
      row[validationKey].valid = true;
      row[validationKey].message = null;
    });
  }

  validateRows(rows) {
    const anyInValid = rows.some((row) => {
      return Object.values(TbRowValidator.VALIDATION_OPTIONS).some(
        (option) => !row[option.validationKey].valid
      );
    });
    if (anyInValid)
      return { invalid: true, message: TbRowValidator.GENERIC_MESSAGE };
    return { invalid: false, message: null };
  }
}

class TmRowValidator {
  static GENERIC_MESSAGE = "无法保存，部分数据不符合规范，详见句段内错误提示";
  static MAX_CHARACTER_LENGTH = 20000;

  static VALIDATION_OPTIONS = {
    src: {label: "原文", validationKey: "srcValidation"},
    tgt: {label: "译文", validationKey: "tgtValidation"},
  };
  static INVALID_ROW_RANGE_OPTIONS = [
    Object.values(TmRowValidator.VALIDATION_OPTIONS),
    [TmRowValidator.VALIDATION_OPTIONS.src],
    [TmRowValidator.VALIDATION_OPTIONS.tgt],
  ];


  attachValidationOption(row) {
    Object.values(TmRowValidator.VALIDATION_OPTIONS).forEach((option) => {
      row[option.validationKey] = {
        valid: true,
        message: null,
      };
    });
  }

  validate(row) {
    const cells = Object.keys(TmRowValidator.VALIDATION_OPTIONS).map((key) => {
      return {
        ...TmRowValidator.VALIDATION_OPTIONS[key],
        value: row[`${key}Text`] || "",
      };
    });
    const dependentIds = uniq(cells.map(cell => row[cell.validationKey].dependentId).filter(id => Boolean(id)));
    for (const cell of cells) {
      const { label, value } = cell;
      const validationOption = row[cell.validationKey];
      if (isBlank(value)) {
        validationOption.valid = false;
        validationOption.message = `${label}不能为空`;
        validationOption.dependentId = "";
        continue;
      }
      if (value.trim().length > TmRowValidator.MAX_CHARACTER_LENGTH) {
        validationOption.valid = false;
        validationOption.message = `${label}不能超过${TmRowValidator.MAX_CHARACTER_LENGTH}字符`;
        validationOption.dependentId = "";
        continue;
      }
      validationOption.valid = true;
      validationOption.message = null;
      validationOption.dependentId = "";
    }
    return dependentIds;
  }

  invalidate(row, invalidDatum) {
    if (isBlank(invalidDatum.message)) return;
    TmRowValidator.INVALID_ROW_RANGE_OPTIONS[invalidDatum.range].forEach((option) => {
      row[option.validationKey] = {
        valid: false,
        message: invalidDatum.message,
        dependentId: invalidDatum.dependentId
      };
    });
  }

  checkRows(rows) {
    const anyInValid = rows.some((tu) => {
      return Object.values(TmRowValidator.VALIDATION_OPTIONS).some(
        (option) => !tu[option.validationKey].valid
      );
    });
    if (anyInValid)
      return { invalid: true, message: TmRowValidator.GENERIC_MESSAGE };
    return { invalid: false, message: null };
  }
}

export {
  DragObjectIdentity,
  StudentDragIdentity,
  DragContext,
  AssetActionHandler,
  AssetImportValidator,
  AssetExportValidator,
  ExportValidationError,
  ImportValidationError,
  TmRowValidator,
  TbRowValidator,
  ExportValidator,
  FileDownloadWarningError,
  TbMetadataValidator,
  TRANSFER_ITEM_TYPE,
  transferOptions,
  permissionOptions,
  ASSET_ACTION,
  DRAG_CONTAINER,
  ASSET_TYPE,
  TM_EXCEL_TEMPLATE_URL,
  TB_EXCEL_TEMPLATE_URL,
  TM_EXPORT_OPTIONS,
  TB_EXPORT_OPTIONS,
  TM_IGNORE_CASES,
  TB_IGNORE_CASES,
  ERROR_CASES,
  validateAssetState,
  downloadFile,
  refreshRows,
  getIgnoreLangField,
  QA_LEVEL,
  getQALevelText,
};
